import React from 'react';
import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';

//types
import type {FilterState, FilterAction} from '../../types';

//hooks
import {useISOCode} from '../../hooks/useISOCodeClient';

const sortingOptionsZH = [
  {
    label: '預設',
    value: 'manual',
  },
  {
    label: '最暢銷產品',
    value: 'best-selling',
  },
  {
    label: '產品 (最新上架)',
    value: 'published-descending',
  },
  {
    label: '價錢 (低至高)',
    value: 'price-ascending',
  },
  {
    label: '價錢 (高至低)',
    value: 'price-descending',
  },
  {
    label: '產品 (A至Z)',
    value: 'title-ascending',
  },
  {
    label: '產品 (Z至A)',
    value: 'title-descending',
  },
];
const sortingOptions = [
  {
    label: 'Manual',
    value: 'manual',
  },
  {
    label: 'Best Selling',
    value: 'best-selling',
  },
  {
    label: 'Newest to oldest',
    value: 'published-descending',
  },
  {
    label: 'Price ascending',
    value: 'price-ascending',
  },
  {
    label: 'Price descending',
    value: 'price-descending',
  },
  {
    label: 'Title ascending',
    value: 'title-ascending',
  },
  {
    label: 'Title descending',
    value: 'title-descending',
  },
];

interface Props {
  state: FilterState;
  dispatch: React.Dispatch<FilterAction>;
  refetch: any;
}

export default function CollectionSorting({state, dispatch, refetch}: Props) {
  const setSelectedSortingOption = (option: string) => {
    dispatch({type: 'SET_SORTING', payload: option});
    setTimeout(() => {
      refetch();
    }, 500);
  };

  const {languageCode} = useISOCode();

  const sortingOptionInMatchedLocale =
    languageCode === 'zh_TW' ? sortingOptionsZH : sortingOptions;

  const selectedSortingOption = sortingOptionInMatchedLocale?.find(
    (o) => o.value === state.sort,
  );
  return (
    <Select.Root
      value={state.sort}
      onValueChange={(value) => {
        const selectedOptions = sortingOptionInMatchedLocale?.find(
          (o) => o.value === value,
        );
        if (selectedOptions) {
          setSelectedSortingOption(selectedOptions.value);
        }
      }}
    >
      <Select.Trigger className="SelectTrigger" aria-label="Sorting Options">
        <div className="">
          <span className="font-semibold">
            {languageCode === 'zh_TW' ? '排序' : 'Sort by'}:{' '}
          </span>
          <Select.Value className="text-xs">
            {selectedSortingOption && selectedSortingOption?.label}
          </Select.Value>
        </div>
        <Select.Icon className="SelectIcon">
          <svg
            viewBox="0 0 24 24"
            className="w-2 rotate-180"
            xmlns="http://www.w3.org/2000/svg"
            fillRule="evenodd"
            clipRule="evenodd"
          >
            <path d="M23.245 20l-11.245-14.374-11.219 14.374-.781-.619 12-15.381 12 15.391-.755.609z" />
          </svg>
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content className="SelectContent">
          <Select.ScrollUpButton className="SelectScrollButton">
            <svg
              viewBox="0 0 24 24"
              className="w-2"
              xmlns="http://www.w3.org/2000/svg"
              fillRule="evenodd"
              clipRule="evenodd"
            >
              <path d="M23.245 20l-11.245-14.374-11.219 14.374-.781-.619 12-15.381 12 15.391-.755.609z" />
            </svg>
          </Select.ScrollUpButton>
          <Select.Viewport className="SelectViewport">
            {sortingOptionInMatchedLocale?.map((o) => (
              <SelectItem key={`sorting-${o.value}`} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </Select.Viewport>
          <Select.ScrollDownButton className="SelectScrollButton">
            <svg
              viewBox="0 0 24 24"
              className="w-2 rotate-180"
              xmlns="http://www.w3.org/2000/svg"
              fillRule="evenodd"
              clipRule="evenodd"
            >
              <path d="M23.245 20l-11.245-14.374-11.219 14.374-.781-.619 12-15.381 12 15.391-.755.609z" />
            </svg>
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

const SelectItem = ({
  children,
  className,
  value,
}: {
  children: React.ReactNode;
  className?: string;
  value: string;
}) => {
  return (
    <Select.Item className={clsx('SelectItem', className)} value={value}>
      <Select.ItemText>{children}</Select.ItemText>
      <Select.ItemIndicator className="SelectItemIndicator">
        <svg
          viewBox="0 0 24 24"
          className="w-3 fill-current"
          xmlns="http://www.w3.org/2000/svg"
          fillRule="evenodd"
          clipRule="evenodd"
        >
          <path d="M21 6.285l-11.16 12.733-6.84-6.018 1.319-1.49 5.341 4.686 9.865-11.196 1.475 1.285z" />
        </svg>
      </Select.ItemIndicator>
    </Select.Item>
  );
};
